import { monaco } from './monaco-config'

export interface MonacoEditorOptions {
  container: HTMLElement
  initialValue: string
  onChange?: (value: string) => void
  onLineChange?: (line: number) => void
  onBreakpointChange?: (breakpoints: number[]) => void
}

export class MonacoEditor {
  public editor: monaco.editor.IStandaloneCodeEditor
  private options: MonacoEditorOptions
  private decorations: string[] = []
  private breakpointDecorations: string[] = []
  private breakpoints: Set<number> = new Set()
  private currentHighlightLine: number = 0
  private animationFrame: number | null = null
  
  constructor(options: MonacoEditorOptions) {
    this.options = options
    
    // Monacoエディターを作成
    this.editor = monaco.editor.create(options.container, {
      value: options.initialValue,
      language: 'c',
      theme: 'vs-dark',
      fontSize: 14,
      fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace",
      minimap: {
        enabled: false,
      },
      scrollBeyondLastLine: false,
      automaticLayout: true,
      lineNumbers: 'on',
      renderWhitespace: 'selection',
      scrollbar: {
        verticalScrollbarSize: 10,
        horizontalScrollbarSize: 10,
      },
      overviewRulerLanes: 0,
      hideCursorInOverviewRuler: true,
      overviewRulerBorder: false,
      lineNumbersMinChars: 3,
      roundedSelection: false,
    })
    
    // C言語のカスタム設定
    this.setupCLanguage()
    
    // イベントリスナーを設定
    this.setupEventListeners()
    
    // ブレークポイント機能を設定
    this.setupBreakpoints()
  }
  
  /**
   * C言語の設定
   */
  private setupCLanguage(): void {
    // C言語のトークン定義
    monaco.languages.register({ id: 'c' })
    
    monaco.languages.setMonarchTokensProvider('c', {
      keywords: [
        'auto', 'break', 'case', 'char', 'const', 'continue', 'default', 'do',
        'double', 'else', 'enum', 'extern', 'float', 'for', 'goto', 'if',
        'int', 'long', 'register', 'return', 'short', 'signed', 'sizeof', 'static',
        'struct', 'switch', 'typedef', 'union', 'unsigned', 'void', 'volatile', 'while',
      ],
      
      typeKeywords: [
        'int', 'float', 'char', 'double', 'long', 'short', 'signed', 'unsigned', 'void',
      ],
      
      operators: [
        '=', '>', '<', '!', '~', '?', ':', '==', '<=', '>=', '!=',
        '&&', '||', '++', '--', '+', '-', '*', '/', '&', '|', '^', '%',
        '<<', '>>', '>>>', '+=', '-=', '*=', '/=', '&=', '|=', '^=',
        '%=', '<<=', '>>=', '>>>=',
      ],
      
      // シンボル定義
      symbols: /[=><!~?:&|+\-*\/\^%]+/,
      
      // エスケープシーケンス
      escapes: /\\(?:[abfnrtv\\"']|x[0-9A-Fa-f]{1,4}|u[0-9A-Fa-f]{4}|U[0-9A-Fa-f]{8})/,
      
      // トークナイザー
      tokenizer: {
        root: [
          // 識別子とキーワード
          [/[a-z_$][\w$]*/, {
            cases: {
              '@typeKeywords': 'keyword',
              '@keywords': 'keyword',
              '@default': 'identifier',
            },
          }],
          [/[A-Z][\w\$]*/, 'type.identifier'],
          
          // 空白
          { include: '@whitespace' },
          
          // デリミタと演算子
          [/[{}()\[\]]/, '@brackets'],
          [/[<>](?!@symbols)/, '@brackets'],
          [/@symbols/, {
            cases: {
              '@operators': 'operator',
              '@default': '',
            },
          }],
          
          // 数値
          [/\d*\.\d+([eE][\-+]?\d+)?[fFdD]?/, 'number.float'],
          [/0[xX][0-9a-fA-F]+/, 'number.hex'],
          [/\d+/, 'number'],
          
          // デリミタ
          [/[;,.]/, 'delimiter'],
          
          // 文字列
          [/"([^"\\]|\\.)*$/, 'string.invalid'],
          [/"/, { token: 'string.quote', bracket: '@open', next: '@string' }],
          
          // 文字
          [/'[^\\']'/, 'string'],
          [/(')(@escapes)(')/, ['string', 'string.escape', 'string']],
          [/'/, 'string.invalid'],
        ],
        
        string: [
          [/[^\\"]+/, 'string'],
          [/@escapes/, 'string.escape'],
          [/\\./, 'string.escape.invalid'],
          [/"/, { token: 'string.quote', bracket: '@close', next: '@pop' }],
        ],
        
        whitespace: [
          [/[ \t\r\n]+/, 'white'],
          [/\/\*/, 'comment', '@comment'],
          [/\/\/.*$/, 'comment'],
        ],
        
        comment: [
          [/[^\/*]+/, 'comment'],
          [/\/\*/, 'comment', '@push'],
          ['\\*/', 'comment', '@pop'],
          [/[\/*]/, 'comment'],
        ],
      },
    })
    
    // C言語のオートコンプリート
    monaco.languages.registerCompletionItemProvider('c', {
      provideCompletionItems: (model, position) => {
        const word = model.getWordUntilPosition(position)
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        }
        
        const suggestions: monaco.languages.CompletionItem[] = [
          // ループ構文
          {
            label: 'for',
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: 'for (int ${1:i} = ${2:0}; ${1:i} < ${3:10}; ${1:i}++) {\n\t$0\n}',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'for loop',
            range,
          },
          {
            label: 'while',
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: 'while (${1:condition}) {\n\t$0\n}',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'while loop',
            range,
          },
          {
            label: 'do-while',
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: 'do {\n\t$0\n} while (${1:condition});',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'do-while loop',
            range,
          },
          // 変数宣言
          {
            label: 'int',
            kind: monaco.languages.CompletionItemKind.Keyword,
            insertText: 'int ${1:variable} = ${2:0};',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Integer variable declaration',
            range,
          },
        ]
        
        return { suggestions }
      },
    })
  }
  
  /**
   * イベントリスナーを設定
   */
  private setupEventListeners(): void {
    // テキスト変更時
    this.editor.onDidChangeModelContent(() => {
      if (this.options.onChange) {
        const value = this.editor.getValue()
        this.options.onChange(value)
      }
    })
    
    // カーソル位置変更時
    this.editor.onDidChangeCursorPosition((e) => {
      if (this.options.onLineChange) {
        this.options.onLineChange(e.position.lineNumber)
      }
    })
  }
  
  /**
   * 値を取得
   */
  getValue(): string {
    return this.editor.getValue()
  }
  
  /**
   * 値を設定
   */
  setValue(value: string): void {
    this.editor.setValue(value)
  }
  
  /**
   * 行をハイライト
   */
  highlightLine(lineNumber: number): void {
    if (this.currentHighlightLine === lineNumber) return
    
    this.currentHighlightLine = lineNumber
    
    // アニメーション付きで行を移動
    if (lineNumber > 0) {
      this.animateLineHighlight(lineNumber)
    } else {
      // ハイライトをクリア
      this.decorations = this.editor.deltaDecorations(this.decorations, [])
    }
  }
  
  /**
   * 行ハイライトをアニメーション
   */
  private animateLineHighlight(lineNumber: number): void {
    // スムーズにスクロール
    this.editor.revealLineInCenterIfOutsideViewport(lineNumber, monaco.editor.ScrollType.Smooth)
    
    // ハイライトを更新
    this.decorations = this.editor.deltaDecorations(this.decorations, [
      {
        range: new monaco.Range(lineNumber, 1, lineNumber, 1),
        options: {
          isWholeLine: true,
          className: 'monaco-editor-highlight-line monaco-editor-highlight-line-animated',
          glyphMarginClassName: 'monaco-editor-highlight-glyph monaco-editor-highlight-glyph-animated',
        },
      },
    ])
    
    // アニメーション終了後にクラスを更新
    setTimeout(() => {
      this.decorations = this.editor.deltaDecorations(this.decorations, [
        {
          range: new monaco.Range(lineNumber, 1, lineNumber, 1),
          options: {
            isWholeLine: true,
            className: 'monaco-editor-highlight-line',
            glyphMarginClassName: 'monaco-editor-highlight-glyph',
          },
        },
      ])
    }, 300)
  }
  
  /**
   * エラー行をマーク
   */
  setErrors(errors: Array<{ line: number; message: string }>): void {
    const markers: monaco.editor.IMarkerData[] = errors.map(error => ({
      severity: monaco.MarkerSeverity.Error,
      startLineNumber: error.line,
      startColumn: 1,
      endLineNumber: error.line,
      endColumn: 1000,
      message: error.message,
    }))
    
    monaco.editor.setModelMarkers(this.editor.getModel()!, 'c-loop-visualizer', markers)
  }
  
  /**
   * エディターを破棄
   */
  dispose(): void {
    this.editor.dispose()
  }
  
  /**
   * フォーカスを設定
   */
  focus(): void {
    this.editor.focus()
  }
  
  /**
   * レイアウトを更新
   */
  layout(): void {
    this.editor.layout()
  }
  
  /**
   * ブレークポイント機能を設定
   */
  private setupBreakpoints(): void {
    // グリフマージンをクリックしてブレークポイントを設定
    this.editor.onMouseDown((e) => {
      if (e.target.type === monaco.editor.MouseTargetType.GUTTER_GLYPH_MARGIN) {
        const lineNumber = e.target.position!.lineNumber
        this.toggleBreakpoint(lineNumber)
      }
    })
  }
  
  /**
   * ブレークポイントを切り替え
   */
  private toggleBreakpoint(lineNumber: number): void {
    if (this.breakpoints.has(lineNumber)) {
      this.breakpoints.delete(lineNumber)
    } else {
      this.breakpoints.add(lineNumber)
    }
    
    this.updateBreakpointDecorations()
    
    if (this.options.onBreakpointChange) {
      this.options.onBreakpointChange(Array.from(this.breakpoints).sort((a, b) => a - b))
    }
  }
  
  /**
   * ブレークポイントのデコレーションを更新
   */
  private updateBreakpointDecorations(): void {
    const newDecorations = Array.from(this.breakpoints).map(lineNumber => ({
      range: new monaco.Range(lineNumber, 1, lineNumber, 1),
      options: {
        isWholeLine: false,
        glyphMarginClassName: 'monaco-editor-breakpoint-glyph',
        stickiness: monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges,
      },
    }))
    
    this.breakpointDecorations = this.editor.deltaDecorations(
      this.breakpointDecorations,
      newDecorations
    )
  }
  
  /**
   * ブレークポイントを取得
   */
  getBreakpoints(): number[] {
    return Array.from(this.breakpoints).sort((a, b) => a - b)
  }
  
  /**
   * ブレークポイントをクリア
   */
  clearBreakpoints(): void {
    this.breakpoints.clear()
    this.updateBreakpointDecorations()
    
    if (this.options.onBreakpointChange) {
      this.options.onBreakpointChange([])
    }
  }
}